// /app/frontend/src/pages/public/DPIAIntakeFormPage.jsx
// Public DPIA Intake Questionnaire — no authentication required.
// Implements the full spec (Assura Risk Module Suggestions v1.0, Part 3):
//   • 9 sections, all spec-required fields
//   • Radios where spec says radio; checkboxes where spec says checkbox
//   • 7 improvements: late-launched callout, LIA conditional, dynamic
//     third-party rows, cross-border Yes/No/Unsure, AI examples, late
//     declaration checkpoint, pre-fill from pre-screen
//   • Full validation covering all required + conditional fields
//   • Pre-populated form_data (from pre-screen) loads from token response

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { dpiaIntakeAPI, tempGuestAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Shield, Save, Send, Loader2, Clock, AlertCircle,
  ChevronRight, ChevronLeft, Users, Database, Share2, Scale,
  Zap, CheckCircle, Plus, Trash2, Upload, FileText, Download,
} from 'lucide-react';
import debounce from 'lodash/debounce';

const MAX_FILE_BYTES = 10 * 1024 * 1024;  // 10 MB — matches backend
const MAX_FILES = 5;                      // matches backend

function humanFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

// ─── Option lists (spec §S2.1, S2.3, S2.4 and S4.1) ─────────────────────────

const SUBJECT_OPTIONS = [
  'Employees', 'Contractors', 'Job applicants', 'Customers', 'Public',
  'Children under 18', 'Patients', 'Students', 'Financially vulnerable', 'Other',
];

const DATA_TYPE_OPTIONS = [
  'Full Name', 'Email Address', 'Phone Number', 'Home Address', 'Date of Birth',
  'Government ID (NIS, TRN, etc.)', 'Financial Information', 'Health/Medical Data',
  'Biometric Data', 'Location Data', 'Employment History', 'Education Records',
  'Photos/Images', 'Video/Audio', 'IP Address', 'Device Information', 'Other',
];

const SPECIAL_CATEGORY_OPTIONS = [
  'Health', 'Biometric', 'Racial or ethnic origin', 'Religious beliefs',
  'Political opinions', 'Trade union', 'Genetic', 'Sexual orientation',
  'Criminal records', 'None',
];

const LAWFUL_BASIS_OPTIONS = [
  { value: 'consent', label: 'Consent', description: 'Individual has given clear consent' },
  { value: 'contract', label: 'Contract', description: 'Necessary for a contract with the individual' },
  { value: 'legal_obligation', label: 'Legal obligation', description: 'Required by law' },
  { value: 'vital_interests', label: 'Vital interests', description: "To protect someone's life" },
  { value: 'public_task', label: 'Public task', description: 'Official function or task in public interest' },
  { value: 'legitimate_interests', label: 'Legitimate interests', description: "Business interest that doesn't override rights" },
];

const VOLUME_OPTIONS = [
  { value: '<100', label: 'Fewer than 100' },
  { value: '100-1k', label: '100 – 1,000' },
  { value: '1k-10k', label: '1,001 – 10,000' },
  { value: '>10k', label: 'More than 10,000' },
  { value: 'unknown', label: 'Unknown' },
];

const EXISTING_OR_NEW_OPTIONS = [
  { value: 'existing', label: 'Data already exists in our systems' },
  { value: 'new', label: 'Newly collected for this project' },
  { value: 'mix', label: 'Mix of both' },
];

const LESS_DATA_OPTIONS = [
  { value: 'no', label: 'No — personal data is essential' },
  { value: 'yes_chose_not', label: 'Yes, but we chose not to' },
  { value: 'possibly_uninvestigated', label: "Possibly — we haven't investigated" },
];

const SECURITY_REVIEW_OPTIONS = [
  { value: 'approved', label: 'Yes, approved' },
  { value: 'in_progress', label: 'Yes, review in progress' },
  { value: 'not_yet', label: 'Not yet' },
  { value: 'not_applicable', label: 'Not applicable' },
];

const STEPS = [
  { id: 1, title: 'Project Details', icon: Shield },
  { id: 2, title: 'Personal Data', icon: Users },
  { id: 3, title: 'Data Usage', icon: Database },
  { id: 4, title: 'Legal Basis', icon: Scale },
  { id: 5, title: 'Necessity', icon: AlertCircle },
  { id: 6, title: 'Access', icon: Share2 },
  { id: 7, title: 'Technology & AI', icon: Zap },
  { id: 8, title: 'Consultation', icon: Users },
  { id: 9, title: 'Declaration', icon: CheckCircle },
];

const EMPTY_FORM = {
  // S1
  project_name: '', department: '', project_owner: '', project_lead: '',
  go_live_date: '', already_launched: false,
  project_description: '', business_benefit: '',
  // S2
  categories_of_individuals: [], data_volume: '', data_types: [],
  special_categories: [], data_existing_or_new: '',
  // S3
  data_collection_method: '', data_usage_method: '',
  data_storage_method: '', data_retention_period: '', data_deletion_method: '',
  // S4
  lawful_basis: '', lawful_basis_explanation: '',
  lia_completed: '', special_category_basis: '',
  // S5
  less_data_option: '', less_data_explanation: '',
  alternatives_considered: '', function_creep_prevention: '',
  // S6
  internal_sharing: '', internal_sharing_details: '',
  external_sharing: '', third_parties: [],
  cross_border_transfer: '', transfer_country: '', transfer_mechanism: '',
  // S7
  automated_or_ai: '', ai_description: '',
  solely_automated_decisions: '', monitoring: '', monitoring_description: '',
  // S8
  internal_consultations: '',
  external_advisors: '', external_advisors_details: '',
  security_review: '', other_concerns: '',
  // S9
  additional_notes: '', late_declaration: false, declaration_accepted: false,
};

// ─── Reusable radio helper (keeps keyboard behaviour accessible) ───────────

const RadioGroup = ({ value, onChange, options, name, testId }) => (
  <div className="space-y-2">
    {options.map(opt => (
      <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
        <input
          type="radio"
          name={name}
          value={opt.value}
          checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          data-testid={testId ? `${testId}-${opt.value}` : undefined}
          className="h-4 w-4 text-teal-600 focus:ring-teal-500"
        />
        <span className="text-sm">{opt.label}</span>
      </label>
    ))}
  </div>
);

// ─── Validation ─────────────────────────────────────────────────────────────

function validateForm(d) {
  const errs = {};

  // S1
  if (!d.project_name?.trim()) errs.project_name = 'Project name is required';
  else if (d.project_name.length > 200) errs.project_name = 'Max 200 characters';
  if (!d.department?.trim()) errs.department = 'Department is required';
  if (!d.project_owner?.trim()) errs.project_owner = 'Project owner is required';
  if (!d.go_live_date) errs.go_live_date = 'Proposed go-live date is required';
  if (!d.project_description?.trim()) errs.project_description = 'Brief description is required';
  else if (d.project_description.trim().length < 50) errs.project_description = 'Please provide at least 50 characters';
  if (!d.business_benefit?.trim()) errs.business_benefit = 'Business benefit is required';

  // S2
  if (!(d.categories_of_individuals || []).length) errs.categories_of_individuals = 'Select at least one subject group';
  if (!d.data_volume) errs.data_volume = 'Volume is required';
  if (!(d.data_types || []).length) errs.data_types = 'Select at least one data type';
  if (!d.data_existing_or_new) errs.data_existing_or_new = 'Please indicate existing vs new';

  // S3
  if (!d.data_collection_method?.trim()) errs.data_collection_method = 'How data is collected is required';
  if (!d.data_usage_method?.trim()) errs.data_usage_method = 'What data is used for is required';
  if (!d.data_storage_method?.trim()) errs.data_storage_method = 'Where data is stored is required';
  if (!d.data_retention_period?.trim()) errs.data_retention_period = 'Retention period is required';

  // S4
  if (!d.lawful_basis) errs.lawful_basis = 'Select a legal basis';
  if (!d.lawful_basis_explanation?.trim()) errs.lawful_basis_explanation = 'Explanation is required';
  if (d.lawful_basis === 'legitimate_interests' && !d.lia_completed) {
    errs.lia_completed = 'LIA status is required when using Legitimate Interests';
  }
  const hasSpecialCats = (d.special_categories || []).some(c => c && c !== 'None');
  if (hasSpecialCats && !d.special_category_basis?.trim()) {
    errs.special_category_basis = 'Additional legal condition is required for special category data';
  }

  // S5
  if (!d.less_data_option) errs.less_data_option = 'Please answer';
  if (['yes_chose_not', 'possibly_uninvestigated'].includes(d.less_data_option) && !d.less_data_explanation?.trim()) {
    errs.less_data_explanation = 'Please explain';
  }
  if (!d.alternatives_considered?.trim()) errs.alternatives_considered = 'Required';
  if (!d.function_creep_prevention?.trim()) errs.function_creep_prevention = 'Required';

  // S6
  if (!d.internal_sharing) errs.internal_sharing = 'Please answer';
  if (!d.external_sharing) errs.external_sharing = 'Please answer';
  if (d.external_sharing === 'yes') {
    if (!(d.third_parties || []).length) {
      errs.third_parties = 'Add at least one row';
    } else {
      (d.third_parties || []).forEach((tp, i) => {
        if (!tp.name?.trim() || !tp.service?.trim() || !tp.country?.trim() || !tp.contract || !tp.due_diligence) {
          errs[`third_parties_${i}`] = 'Complete all columns';
        }
      });
    }
  }
  if (d.cross_border_transfer === 'yes') {
    if (!d.transfer_country?.trim()) errs.transfer_country = 'Required';
    if (!d.transfer_mechanism?.trim()) errs.transfer_mechanism = 'Required';
  }

  // S7
  if (!d.automated_or_ai) errs.automated_or_ai = 'Please answer';
  if (d.automated_or_ai === 'yes') {
    if (!d.ai_description?.trim()) errs.ai_description = 'Describe the AI / automated system';
    if (!d.solely_automated_decisions) errs.solely_automated_decisions = 'Please answer';
  }
  if (!d.monitoring) errs.monitoring = 'Please answer';
  if (d.monitoring === 'yes' && !d.monitoring_description?.trim()) {
    errs.monitoring_description = 'Describe the monitoring';
  }

  // S9
  if (!d.declaration_accepted) errs.declaration_accepted = 'Confirmation required';
  if (d.already_launched && !d.late_declaration) errs.late_declaration = 'Late submission declaration required';

  return errs;
}

// ─── Page component ─────────────────────────────────────────────────────────

export default function DPIAIntakeFormPage({ guestMode = false, dpoMode = false }) {
  const { token, intakeId } = useParams();
  const navigate = useNavigate();
  const guestUser = guestMode ? JSON.parse(localStorage.getItem('guest_user') || '{}') : null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [intake, setIntake] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [lastSaved, setLastSaved] = useState(null);
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const [formData, setFormData] = useState(EMPTY_FORM);

  // Supporting documents — stored at the top of the intake doc, separate
  // from form_data, so auto-save doesn't race with uploads.
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Load intake + any pre-populated form_data (spec §Part 3 PRE-POPULATION).
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        let response;
        if (guestMode) {
          response = await tempGuestAPI.getDpia();
        } else if (dpoMode) {
          response = await dpiaIntakeAPI.getById(intakeId);
        } else {
          response = await dpiaIntakeAPI.getPublic(token);
        }
        setIntake(response.data);
        if (response.data.form_data) {
          setFormData(prev => ({ ...prev, ...response.data.form_data }));
        }
        setSupportingDocs(response.data.supporting_documents || []);
        if (response.data.last_saved_at) setLastSaved(new Date(response.data.last_saved_at));
      } catch (err) {
        const status = err.response?.status;
        if (status === 404) setError('not_found');
        else if (status === 410) setError('expired');
        else if (status === 400) setError('completed');
        else setError('server_error');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, intakeId, guestMode, dpoMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Props fixed at mount — capture in refs so debounce closure never reads stale values.
  const guestModeRef = React.useRef(guestMode);
  const dpoModeRef = React.useRef(dpoMode);
  const tokenRef = React.useRef(token);
  const intakeIdRef = React.useRef(intakeId);
  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { intakeIdRef.current = intakeId; }, [intakeId]);

  // Debounced auto-save — created once at mount, reads from refs at call time.
  const debouncedSave = useCallback(
    debounce(async (data) => {
      try {
        setSaving(true);
        if (guestModeRef.current) {
          await tempGuestAPI.saveDpia({ form_data: data });
        } else if (dpoModeRef.current) {
          await dpiaIntakeAPI.saveById(intakeIdRef.current, { form_data: data });
        } else {
          await dpiaIntakeAPI.saveProgress(tokenRef.current, { form_data: data });
        }
        setLastSaved(new Date());
      } catch (e) {
        console.error('Auto-save failed:', e);
      } finally {
        setSaving(false);
      }
    }, 1500),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const updateField = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      debouncedSave(next);
      if (showErrors) setErrors(validateForm(next));
      return next;
    });
  };

  const toggleInArray = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      const next = { ...prev, [field]: updated };
      debouncedSave(next);
      if (showErrors) setErrors(validateForm(next));
      return next;
    });
  };

  // ── Supporting documents (S1 attachments) ─────────────────────────────
  const handleFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the <input> so picking the same file twice still fires onChange.
    e.target.value = '';
    if (!files.length) return;

    const remaining = MAX_FILES - supportingDocs.length;
    if (remaining <= 0) {
      toast.error(`You can attach up to ${MAX_FILES} supporting documents.`);
      return;
    }

    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.message(`Only ${remaining} of ${files.length} files were accepted (max ${MAX_FILES}).`);
    }

    setUploading(true);
    try {
      for (const f of toUpload) {
        if (f.size > MAX_FILE_BYTES) {
          toast.error(`"${f.name}" is larger than 10 MB and was skipped.`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        try {
          const res = await dpiaIntakeAPI.uploadDocument(token, fd);
          setSupportingDocs(prev => [...prev, res.data]);
        } catch (err) {
          const msg = err.response?.data?.detail || err.message || 'Upload failed';
          toast.error(`"${f.name}": ${msg}`);
        }
      }
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = async (fileId) => {
    try {
      await dpiaIntakeAPI.deleteDocument(token, fileId);
      setSupportingDocs(prev => prev.filter(d => d.file_id !== fileId));
      toast.success('Document removed');
    } catch (err) {
      toast.error('Failed to remove document');
    }
  };

  // Third-party (S6.3) dynamic rows.
  const addThirdParty = () => {
    updateField('third_parties', [
      ...(formData.third_parties || []),
      { id: Date.now(), name: '', service: '', country: '', contract: '', due_diligence: '' },
    ]);
  };
  const updateThirdParty = (idx, field, value) => {
    const rows = [...(formData.third_parties || [])];
    rows[idx] = { ...rows[idx], [field]: value };
    updateField('third_parties', rows);
  };
  const removeThirdParty = (idx) => {
    const rows = [...(formData.third_parties || [])];
    rows.splice(idx, 1);
    updateField('third_parties', rows);
  };

  const handleManualSave = async () => {
    try {
      setSaving(true);
      if (guestMode) {
        await tempGuestAPI.saveDpia({ form_data: formData });
      } else if (dpoMode) {
        await dpiaIntakeAPI.saveById(intakeId, { form_data: formData });
      } else {
        await dpiaIntakeAPI.saveProgress(token, { form_data: formData });
      }
      setLastSaved(new Date());
      toast.success('Progress saved');
    } catch {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    setShowErrors(true);

    if (Object.keys(validationErrors).length) {
      // Jump to the first failing section.
      const firstKey = Object.keys(validationErrors)[0];
      const sectionOf = {
        project_name: 1, department: 1, project_owner: 1, go_live_date: 1,
        project_description: 1, business_benefit: 1,
        categories_of_individuals: 2, data_volume: 2, data_types: 2,
        data_existing_or_new: 2,
        data_collection_method: 3, data_usage_method: 3, data_storage_method: 3,
        data_retention_period: 3,
        lawful_basis: 4, lawful_basis_explanation: 4, lia_completed: 4, special_category_basis: 4,
        less_data_option: 5, less_data_explanation: 5, alternatives_considered: 5, function_creep_prevention: 5,
        internal_sharing: 6, external_sharing: 6, third_parties: 6,
        transfer_country: 6, transfer_mechanism: 6,
        automated_or_ai: 7, ai_description: 7, solely_automated_decisions: 7,
        monitoring: 7, monitoring_description: 7,
        declaration_accepted: 9, late_declaration: 9,
      };
      const firstInvalid = firstKey.startsWith('third_parties_') ? 6 : sectionOf[firstKey] || 1;
      setCurrentStep(firstInvalid);
      toast.error(validationErrors[firstKey] || 'Please complete all required fields');
      return;
    }

    try {
      setSubmitting(true);
      if (guestMode) {
        await tempGuestAPI.submitDpia({ form_data: formData, project_name: formData.project_name || 'DPIA Intake' });
        toast.success('Submitted successfully');
        navigate('/guest/portal');
      } else if (dpoMode) {
        const res = await dpiaIntakeAPI.submitById(intakeId, { form_data: formData });
        toast.success(res.data?.message || 'Submitted successfully');
        navigate('/risk/dpia-intake');
      } else {
        const res = await dpiaIntakeAPI.submit(token, { form_data: formData });
        toast.success(res.data?.message || 'Submitted');
        navigate(`/intake/${token}/success`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => setCurrentStep(s => Math.min(STEPS.length, s + 1));
  const handleBack = () => setCurrentStep(s => Math.max(1, s - 1));

  // ─── Render states ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }
  if (error) {
    const messages = {
      expired: 'This intake link has expired. Please request a new one from your DPO.',
      completed: 'This questionnaire has already been submitted.',
      not_found: 'Intake not found. The link may be invalid.',
      server_error: 'We couldn’t load this intake form right now. Please try again in a minute, or contact your DPO if the problem persists.',
    };
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-10 w-10 mx-auto mb-3 text-amber-500" />
            <p className="text-muted-foreground">{messages[error]}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const err = (k) => showErrors && errors[k] ? <p className="text-sm text-red-500 mt-1">{errors[k]}</p> : null;
  const borderErr = (k) => showErrors && errors[k] ? 'border-red-500' : '';

  // ─── Section renderers ────────────────────────────────────────────────────
  const renderStep = () => {
    switch (currentStep) {
      case 1: return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-1">Project Details</h3>
            <p className="text-sm text-muted-foreground">Tell us about your project or initiative.</p>
          </div>
          <div>
            <Label>Project or initiative name <span className="text-red-500">*</span></Label>
            <Input maxLength={200} value={formData.project_name}
              onChange={e => updateField('project_name', e.target.value)}
              className={borderErr('project_name')} data-testid="project-name-input" />
            {err('project_name')}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Department or business unit <span className="text-red-500">*</span></Label>
              <Input value={formData.department} onChange={e => updateField('department', e.target.value)}
                className={borderErr('department')} />
              {err('department')}
            </div>
            <div>
              <Label>Project owner (name and title) <span className="text-red-500">*</span></Label>
              <Input value={formData.project_owner} onChange={e => updateField('project_owner', e.target.value)}
                className={borderErr('project_owner')} />
              {err('project_owner')}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Project lead (name and title)</Label>
              <Input value={formData.project_lead} onChange={e => updateField('project_lead', e.target.value)} />
            </div>
            <div>
              <Label>Proposed go-live / implementation date <span className="text-red-500">*</span></Label>
              <Input type="date" value={formData.go_live_date} onChange={e => updateField('go_live_date', e.target.value)}
                className={borderErr('go_live_date')} data-testid="go-live-date-input" />
              {err('go_live_date')}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="already_launched" checked={formData.already_launched}
              onCheckedChange={c => updateField('already_launched', !!c)}
              data-testid="already-launched-checkbox" />
            <Label htmlFor="already_launched">Has this activity already launched or gone live?</Label>
          </div>
          {formData.already_launched && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded">
              <p className="text-sm text-amber-900 dark:text-amber-100">
                If personal data processing has already started without a DPIA, this should be flagged to the data protection team immediately. You will be asked to confirm this in the final section.
              </p>
            </div>
          )}

          <div>
            <Label>Brief description of the project <span className="text-red-500">*</span></Label>
            <p className="text-xs text-muted-foreground mb-1">What are you doing, and why? (minimum 50 characters)</p>
            <Textarea rows={4} value={formData.project_description}
              onChange={e => updateField('project_description', e.target.value)} className={borderErr('project_description')} />
            {err('project_description')}
          </div>
          <div>
            <Label>What is the business benefit or objective? <span className="text-red-500">*</span></Label>
            <p className="text-xs text-muted-foreground mb-1">What problem does this solve, or what value does it create?</p>
            <Textarea rows={3} value={formData.business_benefit}
              onChange={e => updateField('business_benefit', e.target.value)} className={borderErr('business_benefit')} />
            {err('business_benefit')}
          </div>

          {/* Supporting Documents — spec: multi-file upload on the intake
              step where scope information is captured. 10 MB/file, max 5. */}
          <div className="pt-4 border-t">
            <Label>Supporting documents</Label>
            <p className="text-xs text-muted-foreground mb-3">
              Attach contracts, design docs, DPAs or anything else the DPO may need.
              Up to {MAX_FILES} files, 10 MB each. No external links.
            </p>

            <div className="flex items-center gap-3">
              <label
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium cursor-pointer
                  ${supportingDocs.length >= MAX_FILES
                    ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-60'
                    : 'bg-white dark:bg-slate-900 border-input hover:bg-muted'}`}
              >
                {uploading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Upload className="h-4 w-4" />}
                {uploading ? 'Uploading…' : 'Attach files'}
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFilesPicked}
                  disabled={uploading || supportingDocs.length >= MAX_FILES}
                  data-testid="supporting-docs-input"
                />
              </label>
              <span className="text-xs text-muted-foreground">
                {supportingDocs.length} / {MAX_FILES} attached
              </span>
            </div>

            {supportingDocs.length > 0 && (
              <ul className="mt-3 space-y-2">
                {supportingDocs.map(d => (
                  <li
                    key={d.file_id}
                    className="flex items-center gap-3 px-3 py-2 rounded-md border bg-slate-50 dark:bg-slate-900/50"
                    data-testid={`supporting-doc-row-${d.file_id}`}
                  >
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{d.file_name}</p>
                      <p className="text-xs text-muted-foreground">{humanFileSize(d.size)}</p>
                    </div>
                    <a
                      href={dpiaIntakeAPI.publicDownloadDocumentUrl(token, d.file_id)}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-muted"
                      title="Download"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </a>
                    <button
                      type="button"
                      onClick={() => removeDocument(d.file_id)}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                      data-testid={`supporting-doc-remove-${d.file_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      );

      case 2: return (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-1">About the Personal Data</h3>
          </div>

          <div>
            <Label>Whose personal data will be involved? <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {SUBJECT_OPTIONS.map(s => (
                <label key={s} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={(formData.categories_of_individuals || []).includes(s)}
                    onCheckedChange={() => toggleInArray('categories_of_individuals', s)} />
                  <span className="text-sm">{s}</span>
                </label>
              ))}
            </div>
            {err('categories_of_individuals')}
          </div>

          <div>
            <Label>Roughly how many people's data will be involved? <span className="text-red-500">*</span></Label>
            <div className="mt-2"><RadioGroup name="data_volume" value={formData.data_volume} onChange={v => updateField('data_volume', v)}
              options={VOLUME_OPTIONS} testId="data-volume" /></div>
            {err('data_volume')}
          </div>

          <div>
            <Label>What types of personal data will be involved? <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {DATA_TYPE_OPTIONS.map(s => (
                <label key={s} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={(formData.data_types || []).includes(s)}
                    onCheckedChange={() => toggleInArray('data_types', s)} />
                  <span className="text-sm">{s}</span>
                </label>
              ))}
            </div>
            {err('data_types')}
          </div>

          <div>
            <Label>Does the processing include any special categories?</Label>
            <p className="text-xs text-muted-foreground mb-1">Tick all that apply (tick <em>None</em> if none).</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
              {SPECIAL_CATEGORY_OPTIONS.map(s => (
                <label key={s} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={(formData.special_categories || []).includes(s)}
                    onCheckedChange={() => toggleInArray('special_categories', s)} />
                  <span className="text-sm">{s}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Is the data currently held, or will it be newly collected? <span className="text-red-500">*</span></Label>
            <div className="mt-2"><RadioGroup name="data_existing_or_new" value={formData.data_existing_or_new}
              onChange={v => updateField('data_existing_or_new', v)} options={EXISTING_OR_NEW_OPTIONS} /></div>
            {err('data_existing_or_new')}
          </div>
        </div>
      );

      case 3: return (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold mb-1">How Data Will Be Used</h3></div>
          {[
            ['data_collection_method', 'How will the personal data be collected?', 'e.g. directly from individuals, from an existing system, from a third party', true],
            ['data_usage_method', 'What will the data be used for?', 'Describe all the ways the data will be used, not just the main purpose', true],
            ['data_storage_method', 'Where will the data be stored?', 'e.g. internal server, cloud platform, specific system name', true],
            ['data_retention_period', 'How long will the data be kept?', 'Specify different periods for different data types if they vary', true],
            ['data_deletion_method', 'How will the data be deleted or destroyed when no longer needed?', 'e.g. automated deletion, secure disposal process, archive policy', false],
          ].map(([key, label, hint, required]) => (
            <div key={key}>
              <Label>{label} {required && <span className="text-red-500">*</span>}</Label>
              {hint && <p className="text-xs text-muted-foreground mb-1">{hint}</p>}
              <Textarea rows={3} value={formData[key]} onChange={e => updateField(key, e.target.value)}
                className={borderErr(key)} />
              {err(key)}
            </div>
          ))}
        </div>
      );

      case 4: return (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold mb-1">Legal Basis</h3></div>
          <div>
            <Label>What is your legal basis for processing personal data? <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              {LAWFUL_BASIS_OPTIONS.map(o => {
                const selected = formData.lawful_basis === o.value;
                return (
                  <label key={o.value} className={`p-3 rounded-lg border-2 cursor-pointer ${selected ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' : 'border-border'}`}>
                    <input type="radio" name="lawful_basis" value={o.value} checked={selected}
                      onChange={() => updateField('lawful_basis', o.value)} className="sr-only" />
                    <div><p className="font-medium text-sm">{o.label}</p><p className="text-xs text-muted-foreground">{o.description}</p></div>
                  </label>
                );
              })}
            </div>
            {err('lawful_basis')}
          </div>

          <div>
            <Label>Please explain why this legal basis applies <span className="text-red-500">*</span></Label>
            <Textarea rows={3} value={formData.lawful_basis_explanation}
              onChange={e => updateField('lawful_basis_explanation', e.target.value)} className={borderErr('lawful_basis_explanation')} />
            {err('lawful_basis_explanation')}
          </div>

          {formData.lawful_basis === 'legitimate_interests' && (
            <div className="p-4 border rounded-lg bg-slate-50 dark:bg-slate-900/40">
              <Label>Has a Legitimate Interests Assessment (LIA) been completed? <span className="text-red-500">*</span></Label>
              <div className="mt-2">
                <RadioGroup name="lia_completed" value={formData.lia_completed} onChange={v => updateField('lia_completed', v)}
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                    { value: 'not_applicable', label: 'Not applicable' },
                  ]} testId="lia-completed" />
              </div>
              {formData.lia_completed === 'no' && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded">
                  <p className="text-sm text-amber-900 dark:text-amber-100">
                    A Legitimate Interests Assessment is required before this processing can be approved. Your DPO will arrange this.
                  </p>
                </div>
              )}
              {err('lia_completed')}
            </div>
          )}

          {(formData.special_categories || []).some(c => c && c !== 'None') && (
            <div>
              <Label>Additional legal condition for special-category data <span className="text-red-500">*</span></Label>
              <p className="text-xs text-muted-foreground mb-1">e.g. explicit consent, vital interests, substantial public interest</p>
              <Textarea rows={3} value={formData.special_category_basis}
                onChange={e => updateField('special_category_basis', e.target.value)} className={borderErr('special_category_basis')} />
              {err('special_category_basis')}
            </div>
          )}
        </div>
      );

      case 5: return (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold mb-1">Necessity & Proportionality</h3></div>
          <div>
            <Label>Is it possible to achieve the same objective using less personal data or anonymised data? <span className="text-red-500">*</span></Label>
            <div className="mt-2"><RadioGroup name="less_data_option" value={formData.less_data_option}
              onChange={v => updateField('less_data_option', v)} options={LESS_DATA_OPTIONS} testId="less-data" /></div>
            {err('less_data_option')}
            {['yes_chose_not', 'possibly_uninvestigated'].includes(formData.less_data_option) && (
              <div className="mt-3">
                <Label>Please explain</Label>
                <Textarea rows={3} value={formData.less_data_explanation}
                  onChange={e => updateField('less_data_explanation', e.target.value)} className={borderErr('less_data_explanation')} />
                {err('less_data_explanation')}
              </div>
            )}
          </div>
          <div>
            <Label>Have you considered any less privacy-intrusive alternatives? <span className="text-red-500">*</span></Label>
            <p className="text-xs text-muted-foreground mb-1">e.g. manual processes, aggregated data, opt-in only, local processing. If none viable, explain why.</p>
            <Textarea rows={3} value={formData.alternatives_considered}
              onChange={e => updateField('alternatives_considered', e.target.value)} className={borderErr('alternatives_considered')} />
            {err('alternatives_considered')}
          </div>
          <div>
            <Label>How will you prevent the data from being used beyond its stated purpose? <span className="text-red-500">*</span></Label>
            <p className="text-xs text-muted-foreground mb-1">e.g. access controls, contractual restrictions, technical limitations, audit logging</p>
            <Textarea rows={3} value={formData.function_creep_prevention}
              onChange={e => updateField('function_creep_prevention', e.target.value)} className={borderErr('function_creep_prevention')} />
            {err('function_creep_prevention')}
          </div>
        </div>
      );

      case 6: return (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold mb-1">Who Else Has Access</h3></div>

          <div>
            <Label>Will any personal data be shared with other teams or departments within your organisation? <span className="text-red-500">*</span></Label>
            <div className="mt-2"><RadioGroup name="internal_sharing" value={formData.internal_sharing}
              onChange={v => updateField('internal_sharing', v)}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} testId="internal-sharing" /></div>
            {err('internal_sharing')}
            {formData.internal_sharing === 'yes' && (
              <div className="mt-3">
                <Label>Which departments, and for what purpose?</Label>
                <Textarea rows={2} value={formData.internal_sharing_details}
                  onChange={e => updateField('internal_sharing_details', e.target.value)} />
              </div>
            )}
          </div>

          <div>
            <Label>Will any external organisation, supplier, or system have access to the personal data? <span className="text-red-500">*</span></Label>
            <div className="mt-2"><RadioGroup name="external_sharing" value={formData.external_sharing}
              onChange={v => updateField('external_sharing', v)}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} testId="external-sharing" /></div>
            {err('external_sharing')}
          </div>

          {formData.external_sharing === 'yes' && (
            <div className="space-y-3">
              <Label>List all external organisations or systems that will have access</Label>
              {err('third_parties')}
              {(formData.third_parties || []).map((tp, idx) => (
                <Card key={tp.id || idx} className="border">
                  <CardContent className="pt-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Row {idx + 1}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeThirdParty(idx)} className="text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <Input placeholder="Organisation name" value={tp.name || ''}
                        onChange={e => updateThirdParty(idx, 'name', e.target.value)} />
                      <Input placeholder="Service or system used" value={tp.service || ''}
                        onChange={e => updateThirdParty(idx, 'service', e.target.value)} />
                      <Input placeholder="Country where data processed" value={tp.country || ''}
                        onChange={e => updateThirdParty(idx, 'country', e.target.value)} />
                      <select className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        value={tp.contract || ''} onChange={e => updateThirdParty(idx, 'contract', e.target.value)}>
                        <option value="">Contract / DPA in place?</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="in_progress">In progress</option>
                      </select>
                      <select className="h-10 rounded-md border border-input bg-background px-3 text-sm md:col-span-2"
                        value={tp.due_diligence || ''} onChange={e => updateThirdParty(idx, 'due_diligence', e.target.value)}>
                        <option value="">Due diligence completed?</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="n_a">N/A</option>
                      </select>
                    </div>
                    {showErrors && errors[`third_parties_${idx}`] && (
                      <p className="text-sm text-red-500">{errors[`third_parties_${idx}`]}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
              <Button variant="outline" size="sm" onClick={addThirdParty} data-testid="add-third-party-btn">
                <Plus className="h-4 w-4 mr-1" />Add Row
              </Button>
            </div>
          )}

          <div>
            <Label>Will any personal data be transferred outside your country?</Label>
            <div className="mt-2"><RadioGroup name="cross_border_transfer" value={formData.cross_border_transfer}
              onChange={v => updateField('cross_border_transfer', v)}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'unsure', label: 'Unsure' },
              ]} testId="cross-border" /></div>
            {formData.cross_border_transfer === 'yes' && (
              <div className="mt-3 space-y-3">
                <div>
                  <Label>Which country?</Label>
                  <Input value={formData.transfer_country}
                    onChange={e => updateField('transfer_country', e.target.value)} className={borderErr('transfer_country')} />
                  {err('transfer_country')}
                </div>
                <div>
                  <Label>What transfer mechanism or safeguard is in place?</Label>
                  <p className="text-xs text-muted-foreground mb-1">
                    e.g. Standard Contractual Clauses, consent of the individual, contractual clauses under local data protection law, DPA with adequacy commitments.
                  </p>
                  <Textarea rows={2} value={formData.transfer_mechanism}
                    onChange={e => updateField('transfer_mechanism', e.target.value)} className={borderErr('transfer_mechanism')} />
                  {err('transfer_mechanism')}
                </div>
              </div>
            )}
          </div>
        </div>
      );

      case 7: return (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold mb-1">Technology, AI & Automated Systems</h3></div>

          <div>
            <Label>Will this activity involve any automated decision-making or AI tools that process personal data? <span className="text-red-500">*</span></Label>
            <div className="mt-2"><RadioGroup name="automated_or_ai" value={formData.automated_or_ai}
              onChange={v => updateField('automated_or_ai', v)}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'not_sure', label: 'Not sure' },
              ]} testId="automated-or-ai" /></div>
            {err('automated_or_ai')}
          </div>

          {formData.automated_or_ai === 'yes' && (
            <div className="space-y-3 p-3 bg-slate-50 dark:bg-slate-900/40 border rounded-lg">
              <div>
                <Label>Describe the AI tool or automated system and how it will be used</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  Examples include: AI meeting transcription tools, automated CV screening, credit or risk scoring, fraud detection, customer chatbots that store conversation data, automated scheduling or HR tools.
                </p>
                <Textarea rows={3} value={formData.ai_description}
                  onChange={e => updateField('ai_description', e.target.value)} className={borderErr('ai_description')} />
                {err('ai_description')}
              </div>
              <div>
                <Label>Will individuals be subject to solely automated decisions with legal or significant effects?</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  e.g. automated job application rejection, credit refusal with no human review.
                </p>
                <RadioGroup name="solely_automated_decisions" value={formData.solely_automated_decisions}
                  onChange={v => updateField('solely_automated_decisions', v)}
                  options={[
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                    { value: 'unsure', label: 'Unsure' },
                  ]} />
                {err('solely_automated_decisions')}
              </div>
            </div>
          )}

          <div>
            <Label>Will this activity involve any monitoring, tracking, or recording of individuals? <span className="text-red-500">*</span></Label>
            <p className="text-xs text-muted-foreground mb-1">
              e.g. CCTV, call recording, screen monitoring, GPS tracking, meeting transcription.
            </p>
            <RadioGroup name="monitoring" value={formData.monitoring}
              onChange={v => updateField('monitoring', v)}
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
                { value: 'not_sure', label: 'Not sure' },
              ]} testId="monitoring" />
            {err('monitoring')}
            {formData.monitoring === 'yes' && (
              <div className="mt-3">
                <Label>Please describe</Label>
                <Textarea rows={2} value={formData.monitoring_description}
                  onChange={e => updateField('monitoring_description', e.target.value)} className={borderErr('monitoring_description')} />
                {err('monitoring_description')}
              </div>
            )}
          </div>
        </div>
      );

      case 8: return (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold mb-1">Consultation & Stakeholders</h3></div>
          <div>
            <Label>Who within your organisation have you consulted?</Label>
            <p className="text-xs text-muted-foreground mb-1">e.g. Legal, Information Security, HR, IT — and what was the outcome of each.</p>
            <Textarea rows={3} value={formData.internal_consultations}
              onChange={e => updateField('internal_consultations', e.target.value)} />
          </div>
          <div>
            <Label>Have any external advisors, auditors, or regulators been consulted?</Label>
            <div className="mt-2"><RadioGroup name="external_advisors" value={formData.external_advisors}
              onChange={v => updateField('external_advisors', v)}
              options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} /></div>
            {formData.external_advisors === 'yes' && (
              <div className="mt-3">
                <Label>Name them and summarise input</Label>
                <Textarea rows={2} value={formData.external_advisors_details}
                  onChange={e => updateField('external_advisors_details', e.target.value)} />
              </div>
            )}
          </div>
          <div>
            <Label>Has the project been reviewed by your Information Security team?</Label>
            <div className="mt-2"><RadioGroup name="security_review" value={formData.security_review}
              onChange={v => updateField('security_review', v)} options={SECURITY_REVIEW_OPTIONS} /></div>
          </div>
          <div>
            <Label>Are there any other privacy, legal, or compliance concerns?</Label>
            <p className="text-xs text-muted-foreground mb-1">
              e.g. regulatory enquiries, previous incidents, contractual obligations, concerns raised by staff.
            </p>
            <Textarea rows={3} value={formData.other_concerns}
              onChange={e => updateField('other_concerns', e.target.value)} />
          </div>
        </div>
      );

      case 9: return (
        <div className="space-y-6">
          <div><h3 className="text-lg font-semibold mb-1">Declaration & Submission</h3></div>
          <div>
            <Label>Additional notes or context</Label>
            <Textarea rows={3} value={formData.additional_notes}
              onChange={e => updateField('additional_notes', e.target.value)} />
          </div>

          {formData.already_launched && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded space-y-2">
              <Label className="flex items-start gap-2">
                <Checkbox id="late_declaration" checked={formData.late_declaration}
                  onCheckedChange={c => updateField('late_declaration', !!c)} data-testid="late-declaration-checkbox" />
                <span className="text-sm">
                  I acknowledge that this intake form is being completed after the procurement or deployment has already begun. <span className="text-red-500">*</span>
                </span>
              </Label>
              {err('late_declaration')}
            </div>
          )}

          <div className="p-3 border rounded space-y-2">
            <Label className="flex items-start gap-2">
              <Checkbox id="declaration_accepted" checked={formData.declaration_accepted}
                onCheckedChange={c => updateField('declaration_accepted', !!c)} data-testid="declaration-accepted-checkbox" />
              <span className="text-sm">
                I confirm that the information provided in this form is accurate to the best of my knowledge, and I understand that a DPIA will be carried out based on these answers. <span className="text-red-500">*</span>
              </span>
            </Label>
            {err('declaration_accepted')}
          </div>

          <div className="text-xs text-muted-foreground">
            Submitting as: <strong>{intake?.client_name || 'Unknown submitter'}</strong> · {intake?.client_email || ''}
          </div>
        </div>
      );

      default: return null;
    }
  };

  // ─── Page chrome ──────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-3">
          <Shield className="h-7 w-7" />
          <div>
            <h1 className="text-2xl font-bold">DPIA Intake Questionnaire</h1>
            <p className="text-teal-100 text-sm">
              {intake?.entity_name ? `For ${intake.entity_name} · ` : ''}Welcome{intake?.client_name ? `, ${intake.client_name}` : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="border-b bg-white dark:bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-wrap gap-2">
          {STEPS.map(s => (
            <button key={s.id} onClick={() => setCurrentStep(s.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                currentStep === s.id ? 'bg-teal-500 text-white'
                  : 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300 hover:bg-teal-200'
              }`}
              data-testid={`step-pill-${s.id}`}
            >
              {s.id}. {s.title}
            </button>
          ))}
          <div className="flex items-center gap-2 ml-auto text-xs text-muted-foreground">
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            {lastSaved && !saving && <><Clock className="h-3 w-3" />Saved {lastSaved.toLocaleTimeString()}</>}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card><CardContent className="pt-6">{renderStep()}</CardContent></Card>

        <div className="flex justify-between items-center mt-6">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}>
            <ChevronLeft className="h-4 w-4 mr-1" />Back
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleManualSave} disabled={saving}>
              <Save className="h-4 w-4 mr-1" />Save Progress
            </Button>
            {currentStep < STEPS.length ? (
              <Button onClick={handleNext}>Next<ChevronRight className="h-4 w-4 ml-1" /></Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-intake-btn">
                {submitting ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" />Submitting</>)
                  : (<><Send className="h-4 w-4 mr-1" />Submit</>)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
